import { computeFixedRevenue } from "./computeFixedRevenue";
import { isNonClientProject } from "./nonClientProjects";

export function calculateMargins({
  hoursData,
  sellingRates,
  buyingCosts,
  overhead,
  periodFrom,
  periodTo,
}) {
  const from = new Date(periodFrom);
  const to = new Date(periodTo);

  /* =====================
     Helpers
  ===================== */
  const num = (x) => {
    const v = Number(x);
    return Number.isFinite(v) ? v : 0;
  };

  const overlaps = (r) => {
    const f = new Date(r.date_from);
    const t = new Date(r.date_to);
    return f <= to && t >= from;
  };

  const norm = (s) => String(s ?? "").trim();
  const normKey = (s) => norm(s).toLowerCase();

  const normProject = (s) =>
    norm(s)
      .toUpperCase()
      .replace(/\(.*?\)/g, "")
      .replace(/[_-]+/g, " ")
      .replace(/\s+/g, " ")
      .trim();

  const pickBest = (items) => {
    const o = (items || []).filter(overlaps);
    if (!o.length) return null;
    o.sort((a, b) => new Date(b.date_from) - new Date(a.date_from));
    return o[0];
  };

  // Round a monetary amount to whole cents to avoid IEEE-754 drift across the
  // different aggregations (by-client vs by-consultant vs KPI re-sums). Applied
  // only at final aggregation, never to intermediate per-day accumulators.
  const round2 = (x) => Math.round((num(x) + Number.EPSILON) * 100) / 100;

  // For a single day (ms), return the rate row whose [date_from, date_to]
  // contains that day, latest date_from winning on ties. Mirrors pickBest but
  // for an exact day instead of any-overlap-with-period.
  const pickForDay = (items, dayMs) => {
    let best = null;
    let bestFrom = -Infinity;
    (items || []).forEach((r) => {
      const f = new Date(r.date_from).getTime();
      const t = new Date(r.date_to).getTime();
      if (f <= dayMs && t >= dayMs && f >= bestFrom) {
        best = r;
        bestFrom = f;
      }
    });
    return best;
  };

  // Price a single hours row per day instead of per period: for each day in
  // [from, to] that the row logged hours, select the rate row covering that day
  // (3-tier exact-Key -> empty-Key -> User/Project fallback, most specific tier
  // that has a covering row wins) and accumulate dayHours * rate. This handles
  // a rate that changes mid-period instead of applying one "best" row to the
  // whole window. Falls back to the period-level total when the row has no
  // hours_by_day breakdown. Returns { amount, ratedHours, unratedHours }.
  // `tiers` is an ORDERED array of candidate-row arrays, most specific
  // first. It used to be three fixed arguments; it is a list now because the
  // buy side gained a fourth, user-level tier (see buyTiers).
  const priceRowPerDay = (row, hours, tiers, rateField) => {
    const byDay = row.hours_by_day;
    if (!byDay || Object.keys(byDay).length === 0) {
      // No daily breakdown: fall back to the single-best-row period pricing.
      let r = null;
      for (const t of tiers) {
        r = pickBest(t);
        if (r) break;
      }
      if (!r) return { amount: 0, ratedHours: 0, unratedHours: hours };
      return { amount: hours * num(r[rateField]), ratedHours: hours, unratedHours: 0 };
    }

    let amount = 0;
    let ratedHours = 0;
    let unratedHours = 0;
    Object.entries(byDay).forEach(([dateStr, h]) => {
      const dayHours = num(h);
      if (dayHours <= 0) return;
      const dayMs = new Date(dateStr).getTime();
      if (dayMs < from.getTime() || dayMs > to.getTime()) return; // outside period
      let r = null;
      for (const t of tiers) {
        r = pickForDay(t, dayMs);
        if (r) break;
      }
      if (r) {
        amount += dayHours * num(r[rateField]);
        ratedHours += dayHours;
      } else {
        unratedHours += dayHours;
      }
    });
    return { amount, ratedHours, unratedHours };
  };

  /* =====================
     1) FIXED REVENUE — annual rate prorated by day, latest date_from wins
  ===================== */
  const { fixedRevenueByUserProject, fixedUserProject } = computeFixedRevenue(
    sellingRates,
    periodFrom,
    periodTo
  );
  const fixedRevenueByClient = {};
  Object.entries(fixedRevenueByUserProject).forEach(([up, rev]) => {
    const project = up.split("||")[1];
    fixedRevenueByClient[project] = (fixedRevenueByClient[project] || 0) + rev;
  });

  /* =====================
     2) NON-FIXED rates index
  ===================== */
  const sellByKey = {};
  const sellByUP = {};
  (sellingRates || []).forEach((r) => {
    if (r.fixed) return;
    const k = `${norm(r.User)}||${normProject(r.Project)}||${normKey(r.Key)}`;
    sellByKey[k] = sellByKey[k] || [];
    sellByKey[k].push(r);
    const up = `${norm(r.User)}||${normProject(r.Project)}`;
    sellByUP[up] = sellByUP[up] || [];
    sellByUP[up].push(r);
  });

  const buyByKey = {};
  const buyByUP = {};
  // Cost rows that carry NO project apply to the person whoever they bill.
  // Talexa's internal cost feed emits these: an employee's fully-loaded
  // cost per hour is a property of the PERSON, not of the client they
  // happen to work for that day, so it is stored once instead of being
  // copied onto every project (which is how the Excel drifted).
  const buyByUser = {};
  (buyingCosts || []).forEach((r) => {
    const user = norm(r.User);
    const project = normProject(r.Project);
    if (!project) {
      buyByUser[user] = buyByUser[user] || [];
      buyByUser[user].push(r);
      return;
    }
    const k = `${user}||${project}||${normKey(r.Key)}`;
    buyByKey[k] = buyByKey[k] || [];
    buyByKey[k].push(r);
    const up = `${user}||${project}`;
    buyByUP[up] = buyByUP[up] || [];
    buyByUP[up].push(r);
  });

  // Return the 3-tier candidate arrays (exact Key, empty Key, User/Project) so
  // callers can either pick a single best row (period pricing) or price per day.
  function sellTiers(user, project, key) {
    const kExact = `${norm(user)}||${normProject(project)}||${normKey(key)}`;
    const kFallback = `${norm(user)}||${normProject(project)}||`;
    const up = `${norm(user)}||${normProject(project)}`;
    return [sellByKey[kExact], sellByKey[kFallback], sellByUP[up]];
  }

  // Four tiers on the buy side: the three project-scoped ones, then a
  // person-level fallback for cost rows that carry no project (internals
  // from Talexa). It fires only when the project-scoped tiers miss, so
  // externals — which always arrive with a project — are unaffected.
  function buyTiers(user, project, key) {
    const kExact = `${norm(user)}||${normProject(project)}||${normKey(key)}`;
    const kFallback = `${norm(user)}||${normProject(project)}||`;
    const up = `${norm(user)}||${normProject(project)}`;
    return [
      buyByKey[kExact],
      buyByKey[kFallback],
      buyByUP[up],
      buyByUser[norm(user)],
    ];
  }

  /* =====================
     3) OVERHEAD from period (already prorated by getOverheadForPeriod)
     Equal split per internal consultant, then prorated across their projects by hours
  ===================== */
  const overheadForPeriod = overhead?.annual_total ? num(overhead.annual_total) : 0;

  // Acumular horas por consultor y por (consultor, proyecto) — solo internals
  const internalUserHours = {};
  const internalUserProjectHours = {};

  (hoursData || []).forEach((r) => {
    if (r.__type !== "internal") return;
    const u = norm(r.User);
    const p = normProject(r.Project);
    const h = num(r.total_horas);
    if (!u || !p || h <= 0) return;

    // Exclude internal/non-billable categories (INTERNAL, VACATION, ...) from
    // the overhead distribution: they carry no overhead and are hidden from the
    // views, so letting their hours into the denominator would dilute the
    // overhead on real clients and that diluted share would silently disappear.
    // Overhead is spread over billable client hours only.
    if (isNonClientProject(p)) return;

    internalUserHours[u] = (internalUserHours[u] || 0) + h;
    const k = `${u}||${p}`;
    internalUserProjectHours[k] = (internalUserProjectHours[k] || 0) + h;
  });

  // Equal split: cada consultor interno paga la misma parte del overhead,
  // independientemente de las horas trabajadas.
  // Su parte se distribuye entre sus proyectos proporcionalmente a sus horas.
  const overheadByUserProject = {};
  const numInternalConsultants = Object.keys(internalUserHours).length;
  // Divide by the people who actually RECEIVE a share — i.e. the internals
  // with client hours in the period — so the whole overhead is allocated.
  //
  // This used to divide by the headcount on the overhead row instead, to
  // keep each person's share stable when somebody logged no hours. But the
  // payout loop below only ever pays the consultants in
  // `internalUserHours`, so whenever the headcount was larger the surplus
  // was allocated to NOBODY and vanished: it never reached a client, never
  // entered total cost, and quietly overstated the gross margin by exactly
  // that amount.
  //
  // Koen hit it on 2026-08-06: Talexa reported €192,882 of overhead for
  // 2025 and Margix showed €135,018. Ten people were employed that year but
  // only seven had client hours (two started in November, one left in
  // September), so three shares — €57,865 — evaporated. 2024 lost €31,043
  // the same way; 2023 and 2026 happened to be clean because everyone had
  // hours.
  //
  // Dividing by the recipients also restores what this screen has always
  // claimed to do: "the total overhead cost is determined, this total is
  // equally distributed among the internal consultants" (the Margin
  // Calculation tooltip in Dashboard.jsx). The trade-off is accepted and
  // deliberate — somebody with no client hours in a short period now raises
  // their colleagues' share — because the overhead is real money that can
  // only be recovered from the client work that did happen.
  const overheadDenom = numInternalConsultants;

  if (overheadForPeriod > 0 && numInternalConsultants > 0 && overheadDenom > 0) {
    const overheadPerConsultant = overheadForPeriod / overheadDenom;

    Object.entries(internalUserHours).forEach(([u, totalUserHours]) => {
      Object.entries(internalUserProjectHours)
        .filter(([k]) => k.startsWith(`${u}||`))
        .forEach(([k, h]) => {
          overheadByUserProject[k] = overheadPerConsultant * (h / totalUserHours);
        });
    });
  }

  /* =====================
     5) MAIN LOOP (hours)
  ===================== */
  const byClient = {};
  const overheadApplied = new Set();

  (hoursData || []).forEach((row) => {
    const user = norm(row.User);
    const project = normProject(row.Project);
    const key = normKey(row.Key);
    const hours = num(row.total_horas);
    if (!project || hours <= 0) return;

    // Suppress hourly billing for any (User, Project) that has a fixed rate in
    // the period — independent of the Key (Chrona-imported hours have no Key).
    const isFixedUP = fixedUserProject.has(`${user}||${project}`);

    const sellCandidates = sellTiers(user, project, key);
    const buyCandidates = buyTiers(user, project, key);

    // Revenue: price hourly selling per DAY so a mid-period rate change is honored
    // (e.g. 40/h Jan–Mar then 50/h Apr–Jun) instead of applying one "best" row to
    // the whole window. Fixed-UP rows skip hourly revenue (covered by fixed path).
    const sellPriced = isFixedUP
      ? { amount: 0, ratedHours: 0, unratedHours: hours }
      : priceRowPerDay(row, hours, sellCandidates, "sales_rate");

    // Direct cost: also priced per day (still charged on fixed-rate projects).
    // Unlike revenue this is NEVER suppressed — a fixed retainer covers what
    // we charge, not what the person costs us.
    const buyPriced = priceRowPerDay(row, hours, buyCandidates, "purchase_rate");

    const revenue = sellPriced.amount;
    const directCost = buyPriced.amount;

    let overheadAlloc = 0;
    if (row.__type === "internal") {
      const k = `${user}||${project}`;
      if (!overheadApplied.has(k)) {
        overheadAlloc = num(overheadByUserProject[k]);
        overheadApplied.add(k);
      }
    }

    if (!byClient[project]) {
      byClient[project] = {
        name: project,
        hours: 0,
        hoursMissingRates: 0,
        hoursMissingCosts: 0,
        revenue: 0,
        directCost: 0,
        overhead: 0,
        cost: 0,
        margin: 0,
      };
    }

    byClient[project].hours += hours;
    byClient[project].directCost += directCost;
    byClient[project].overhead += overheadAlloc;

    // Surface hours that produced €0 revenue because no selling rate covered them
    // (finding #10/#23). Fixed-UP hours are NOT "missing" — their revenue comes
    // from the fixed path.
    if (!isFixedUP) {
      byClient[project].hoursMissingRates += sellPriced.unratedHours;
    }

    // Same for the BUY side, which had no counter at all: hours nobody has a
    // cost rate for priced at €0 and inflated the margin with no signal
    // anywhere. That asymmetry is the one the person-level Talexa feed makes
    // easy to hit — a consultant simply absent from it costs nothing.
    byClient[project].hoursMissingCosts += buyPriced.unratedHours;

    byClient[project].revenue += revenue;
  });

  /* =====================
     6) ADD FIXED revenue (already prorated) + finalize
  ===================== */
  // A fixed-fee client may have NO logged hours in the selected period (e.g. a
  // monthly retainer in a month nobody booked time). Make sure it still gets a
  // row so its fixed revenue shows up — otherwise the client table / KPIs /
  // treemap would drop it while the consultant×client table (which already
  // creates such rows) shows it, and the two would disagree.
  Object.keys(fixedRevenueByClient).forEach((project) => {
    if (!byClient[project]) {
      byClient[project] = {
        name: project,
        hours: 0,
        hoursMissingRates: 0,
        hoursMissingCosts: 0,
        revenue: 0,
        directCost: 0,
        overhead: 0,
        cost: 0,
        margin: 0,
      };
    }
  });

  Object.values(byClient).forEach((c) => {
    c.revenue += fixedRevenueByClient[c.name] || 0;
    // Round monetary outputs to whole cents at final aggregation only, so the
    // by-client totals reconcile to the cent with the by-consultant table and
    // the KPI re-sums (finding #52). Intermediate accumulators stay full-precision.
    c.revenue = round2(c.revenue);
    c.directCost = round2(c.directCost);
    c.overhead = round2(c.overhead);
    c.cost = round2(c.directCost + c.overhead);
    c.margin = round2(c.revenue - c.cost);
  });

  // Drop internal / non-billable categories (INTERNAL, VACATION, ...) — they
  // are company info, not clients. Overhead they absorbed leaves with them
  // (not redistributed onto billable clients), so real-client rows keep the
  // exact overhead they already had.
  return Object.values(byClient).filter((c) => !isNonClientProject(c.name));
}